import React, { useState, useEffect } from 'react';
import { XMarkIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { 
  getConsentStatus, 
  getCookiePreferences, 
  saveCookiePreferences,
  CookiePreferences,
  DEFAULT_PREFERENCES
} from '../utils/cookieManager';

// Cookie categories with descriptions
const COOKIE_CATEGORIES = [
  {
    id: 'necessary',
    name: 'Necessary Cookies',
    description: 'These cookies are required for the website to function properly and cannot be disabled.',
    required: true,
  },
  {
    id: 'functional',
    name: 'Functional Cookies',
    description: 'These cookies enable personalized features and functionality on our website.',
    required: false,
  },
  {
    id: 'analytics',
    name: 'Analytics Cookies',
    description: 'These cookies help us understand how visitors interact with our website, helping us improve our services.',
    required: false,
  },
  {
    id: 'marketing',
    name: 'Marketing Cookies',
    description: 'These cookies are used to track visitors across websites to display relevant advertisements.',
    required: false,
  },
];

// Cookie categories are defined in the cookieManager utility

const CookieConsent: React.FC = () => {
  // State for consent status and preferences
  const [consentGiven, setConsentGiven] = useState<boolean | null>(null);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>(DEFAULT_PREFERENCES);
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);

  // Load saved preferences on component mount
  useEffect(() => {
    const savedConsent = getConsentStatus();
    const savedPreferences = getCookiePreferences();
    const showPreferencesFlag = localStorage.getItem('raidu_show_cookie_preferences');

    if (savedConsent !== null) {
      setConsentGiven(savedConsent);
    }

    setPreferences(savedPreferences);
    
    // Check if we should show preferences (triggered from footer)
    if (showPreferencesFlag === 'true') {
      setShowPreferences(true);
      // Clear the flag
      localStorage.removeItem('raidu_show_cookie_preferences');
    }
  }, []);

  // Save preferences to local storage
  const savePreferences = (newPreferences: CookiePreferences, hasConsented: boolean) => {
    saveCookiePreferences(newPreferences, hasConsented);
    setPreferences(newPreferences);
    setConsentGiven(hasConsented);
    setShowPreferences(false);
  };

  // Accept all cookies
  const acceptAll = () => {
    const allEnabled: CookiePreferences = {
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true
    };
    
    savePreferences(allEnabled, true);
  };

  // Accept only necessary cookies
  const acceptNecessary = () => {
    const onlyNecessary: CookiePreferences = {
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false
    };
    
    savePreferences(onlyNecessary, true);
  };

  // Save current preferences
  const saveCurrentPreferences = () => {
    savePreferences(preferences, true);
  };

  // Toggle a specific preference
  const togglePreference = (categoryId: string) => {
    if (categoryId === 'necessary') return; // Cannot toggle necessary cookies
    
    // Type-safe way to update preferences
    const updatedPreferences = { ...preferences };
    
    if (categoryId === 'functional') {
      updatedPreferences.functional = !preferences.functional;
    } else if (categoryId === 'analytics') {
      updatedPreferences.analytics = !preferences.analytics;
    } else if (categoryId === 'marketing') {
      updatedPreferences.marketing = !preferences.marketing;
    }
    
    setPreferences(updatedPreferences);
  };

  // Toggle category expansion
  const toggleCategory = (categoryId: string) => {
    setExpandedCategory(expandedCategory === categoryId ? null : categoryId);
  };

  // Open preferences modal
  const openPreferences = () => {
    setShowPreferences(true);
  };

  // If consent has been given, don't show anything
  if (consentGiven === true) {
    return null;
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 font-inter">
      {/* Main cookie banner */}
      {!showPreferences && (
        <div className="bg-white shadow-lg border-t border-gray-200 p-4 md:p-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-gray-900">Cookie Consent</h2>
                <p className="text-sm text-gray-600 mt-1">
                  We use cookies to enhance your browsing experience, serve personalized ads or content, and analyze our traffic. By clicking "Accept All", you consent to our use of cookies.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={openPreferences}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                >
                  Preferences
                </button>
                <button
                  onClick={acceptNecessary}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                >
                  Necessary Only
                </button>
                <button
                  onClick={acceptAll}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
                >
                  Accept All
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Detailed preferences modal */}
      {showPreferences && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-900">Cookie Preferences</h2>
              <button 
                onClick={() => setShowPreferences(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            <div className="p-4 overflow-y-auto flex-1">
              <p className="text-sm text-gray-600 mb-4">
                We use cookies to ensure the website functions properly, improve your experience, analyze our traffic, and for marketing purposes. You can choose which cookies you want to allow.
              </p>
              
              <div className="space-y-4">
                {COOKIE_CATEGORIES.map((category) => (
                  <div key={category.id} className="border border-gray-200 rounded-lg overflow-hidden">
                    <div 
                      className="p-3 bg-gray-50 flex justify-between items-center cursor-pointer"
                      onClick={() => toggleCategory(category.id)}
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id={`cookie-${category.id}`}
                            checked={category.id === 'necessary' ? preferences.necessary : 
                                    category.id === 'functional' ? preferences.functional :
                                    category.id === 'analytics' ? preferences.analytics :
                                    category.id === 'marketing' ? preferences.marketing : false}
                            onChange={() => togglePreference(category.id)}
                            disabled={category.required}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                        <label 
                          htmlFor={`cookie-${category.id}`}
                          className="text-sm font-medium text-gray-900"
                        >
                          {category.name}
                          {category.required && (
                            <span className="ml-2 text-xs text-gray-500">(Required)</span>
                          )}
                        </label>
                      </div>
                      {expandedCategory === category.id ? (
                        <ChevronUpIcon className="h-5 w-5 text-gray-500" />
                      ) : (
                        <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                      )}
                    </div>
                    
                    {expandedCategory === category.id && (
                      <div className="p-3 border-t border-gray-200 bg-white">
                        <p className="text-sm text-gray-600">{category.description}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
            
            <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
              <button
                onClick={() => setShowPreferences(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={saveCurrentPreferences}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
              >
                Save Preferences
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CookieConsent;
